import { Ref } from 'vue'

import { getAllDescendants, getElement } from '../utils'
import uuid from '@/helpers/uuid'

import type { UseConfig } from '@/components/filepicker/hooks/config'
import type { ItemManager } from '@/components/filepicker/hooks/itemManager'

interface UseHiddenInputFileReturn {
  initHiddenFileInput: (config: {
    config: UseConfig
    dropzone: Ref<HTMLElement>
    itemManager: ItemManager
  }) => void
  destroyHiddenFileInput: () => void
  setMultiple: (multiple: boolean) => void
  triggerClickOnHiddenFileInput: (evt: CustomEvent) => boolean
}

/**
 * Create and hidden input file in order to click on the dropzone clickable area
 * and trigger the file selection
 *
 * @returns {{
 *      initHiddenFileInput: initHiddenFileInput,
 *      destroyHiddenFileInput: destroyHiddenFileInput
 * }}
 */
export default function useHiddenInputFile(): UseHiddenInputFileReturn {
  let hiddenFileInput: HTMLInputElement | null
  let clickableElements: Element[] = []
  const triggerClickOnHiddenFileInput = (evt: CustomEvent | Event) => {
    if (
      !('detail' in evt) ||
      !evt.detail ||
      ('detail' in evt && evt.detail === 1)
    ) {
      // debugger
      if (clickableElements.findIndex((el) => el === evt.target) !== -1) {
        evt.stopPropagation()
        if (hiddenFileInput) {
          hiddenFileInput.click()
        }
      }
      return true
    }
    return false
  }

  const setMultiple = (multiple: boolean) => {
    if (hiddenFileInput && multiple) {
      hiddenFileInput.setAttribute('multiple', 'multiple')
    } else if (hiddenFileInput && !multiple) {
      hiddenFileInput.removeAttribute('multiple')
    }
  }

  const setupHiddenFileInput = ({
    config,
    itemManager,
  }: {
    config: UseConfig
    itemManager: ItemManager
  }) => {
    if (hiddenFileInput && hiddenFileInput.parentNode) {
      hiddenFileInput.parentNode.removeChild(hiddenFileInput)
    }
    hiddenFileInput = document.createElement('input')
    hiddenFileInput.setAttribute('type', 'file')
    if (config.maxFiles === null || config.maxFiles > 1) {
      setMultiple(true)
    }
    if (config.accepts.length > 0) {
      hiddenFileInput.setAttribute('accept', config.accepts.join(','))
    }
    // Make sure that no one can tab in this input field.
    hiddenFileInput.setAttribute('tabindex', '-1')
    hiddenFileInput.style.visibility = 'hidden'
    hiddenFileInput.style.position = 'absolute'
    hiddenFileInput.style.top = '0'
    hiddenFileInput.style.left = '0'
    hiddenFileInput.style.height = '0'
    hiddenFileInput.style.width = '0'
    getElement(config.hiddenInputContainer, 'hiddenInputContainer').appendChild(
      hiddenFileInput,
    )
    hiddenFileInput.addEventListener('change', () => {
      if (hiddenFileInput) {
        const { files } = hiddenFileInput
        if (files) {
          const filesList = [...files]
          filesList.forEach((file) => {
            itemManager.addFile(uuid(), file)
          })
        }
      }
      setupHiddenFileInput({ config, itemManager })
    })
  }

  /**
   *
   * Create the hidden input file
   *
   * @param config - dropzone props
   * @param dropzone - element ref
   * @param addFile - callback that add a new file
   */
  const initHiddenFileInput = ({
    config,
    dropzone,
    itemManager,
  }: {
    config: UseConfig
    dropzone: Ref<HTMLElement>
    itemManager: ItemManager
  }) => {
    if (config.clickable) {
      const element = getElement(
        `.${config.dropzoneMessageClassName}`,
        config.dropzoneMessageClassName,
      )
      const elements = getAllDescendants(element)
      clickableElements = [dropzone.value]
      clickableElements.push(element)
      if (elements.length > 0) {
        clickableElements.push(...elements)
      }
      setupHiddenFileInput({ config, itemManager })
      clickableElements.forEach((el) => {
        el.classList.add('dropzone-clickable')
        el.addEventListener('click', triggerClickOnHiddenFileInput)
      })
    }
  }

  const destroyHiddenFileInput = () => {
    // Remove all events
    clickableElements.forEach((el) => {
      el.removeEventListener('click', triggerClickOnHiddenFileInput)
    })
    // Delete the hidden input file
    if (hiddenFileInput && hiddenFileInput.parentElement) {
      hiddenFileInput.parentElement.removeChild(hiddenFileInput)
      hiddenFileInput = null
    }
  }

  return {
    initHiddenFileInput,
    destroyHiddenFileInput,
    setMultiple,
    triggerClickOnHiddenFileInput,
  }
}
